import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Link } from 'react-router-dom';
const STORAGE_KEY = 'laebel-cookie-consent';
const ease = [0.23, 1, 0.32, 1] as const;
type Choice = 'all' | 'necessary';

/**
 * Cookie notice shown on first visit, in the same frosted dark surface as the menu dock.
 * The choice is remembered in localStorage so it only appears once.
 */
export function CookieNotice() {
  const reduce = useReducedMotion();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let stored: string | null = null;
    try {
      stored = window.localStorage.getItem(STORAGE_KEY);
    } catch {
      stored = null;
    }
    if (stored) return;
    // Let the page settle before the notice appears
    const t = window.setTimeout(() => setOpen(true), 700);
    return () => window.clearTimeout(t);
  }, []);
  const choose = (choice: Choice) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
        choice,
        at: new Date().toISOString()
      }));
    } catch {
      // Storage unavailable (private mode): just close for this visit
    }
    setOpen(false);
  };
  return <AnimatePresence data-id="element-53" data-mp="3i7259otz5zn3">
      {open && <motion.div role="dialog" aria-live="polite" aria-label="Cookie notice" initial={reduce ? {
      opacity: 0
    } : {
      opacity: 0,
      y: 12,
      scale: 0.98
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={reduce ? {
      opacity: 0
    } : {
      opacity: 0,
      y: 12,
      scale: 0.98
    }} transition={{
      duration: 0.24,
      ease
    }} className="fixed bottom-3 left-3 z-[60] w-[calc(100vw-24px)] max-w-[400px] rounded-card border border-white/10 bg-[#1c1c1c]/85 p-5 text-white shadow-[0_24px_60px_rgba(0,0,0,0.28)] backdrop-blur-xl backdrop-saturate-150 md:bottom-5 md:left-5 md:w-[400px] md:p-6" data-id="element-54" data-mp="3n16c8838q7o6">
          <p className="text-[12px] font-medium uppercase tracking-[0.04em] text-white/60" data-id="element-55" data-mp="jlr4jdic4a67">Cookies</p>
          <p className="mt-3 text-[15px] leading-[1.5] text-white/80" data-id="element-56" data-mp="okc8no0pjdzx">
            We use only the cookies needed to run this site — no advertising, no cross-site tracking. Read our{' '}
            <Link to="/legal/cookies" onClick={() => setOpen(false)} className="text-white underline decoration-white/30 underline-offset-4 transition-colors duration-150 hover:decoration-white" data-id="element-57" data-mp="2fongcthxt71c">
              Cookie Policy
            </Link>
            .
          </p>
          <div className="mt-5 flex gap-2" data-id="element-58" data-mp="1wwbhwhuempwh">
            <button type="button" onClick={() => choose('all')} className="h-10 flex-1 rounded-control bg-white px-4 text-[13px] uppercase tracking-[0.04em] text-black transition-colors duration-150 hover:bg-white/85 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black" data-id="element-59" data-mp="1wxcrehvrv4c3">
              Accept
            </button>
            <button type="button" onClick={() => choose('necessary')} className="h-10 flex-1 whitespace-nowrap rounded-control border border-white/30 px-4 text-[13px] uppercase tracking-[0.04em] text-white transition-colors duration-150 hover:border-white hover:bg-white hover:text-black focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-60" data-mp="1e3r13yrzcb91">
              Necessary only
            </button>
          </div>
        </motion.div>}
    </AnimatePresence>;
}